import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { accountCleared } from '../actions';
import type { RootState } from '../store';

export type LoadingState = 'idle' | 'pending' | 'succeeded' | 'failed';

export interface SessionUser {
  uid: string;
  displayName: string | null;
  email: string | null;
  emailVerified: boolean;
  phoneNumber: string | null;
  photoURL: string | null;
}

export interface SessionProfile extends Partial<Omit<Pagcomanda.User, 'createdAt'>> {
  createdAt: string | null;
}

export interface SessionCard {
  id: string;
  data: Pagcomanda.Card;
}

interface SessionState {
  authStatus: 'checking' | 'authenticated' | 'anonymous' | 'failed';
  user: SessionUser | null;
  profileStatus: LoadingState;
  profile: SessionProfile | null;
  cardsStatus: LoadingState;
  cards: SessionCard[];
  error: string | null;
}

const initialState: SessionState = {
  authStatus: 'checking',
  user: null,
  profileStatus: 'idle',
  profile: null,
  cardsStatus: 'idle',
  cards: [],
  error: null,
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    authResolved: (state, action: PayloadAction<SessionUser>) => ({
      ...state,
      authStatus: 'authenticated',
      user: action.payload,
      profileStatus: 'pending',
      profile: null,
      cardsStatus: 'pending',
      cards: [],
      error: null,
    }),
    authFailed: (state, action: PayloadAction<string>) => ({
      ...state,
      authStatus: 'failed',
      error: action.payload,
    }),
    profileReceived: (state, action: PayloadAction<SessionProfile>) => ({
      ...state,
      profileStatus: 'succeeded',
      profile: action.payload,
    }),
    profileFailed: (state, action: PayloadAction<string>) => ({
      ...state,
      profileStatus: 'failed',
      error: action.payload,
    }),
    cardsReceived: (state, action: PayloadAction<SessionCard[]>) => ({
      ...state,
      cardsStatus: 'succeeded',
      cards: action.payload,
    }),
    cardsFailed: (state, action: PayloadAction<string>) => ({
      ...state,
      cardsStatus: 'failed',
      error: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(accountCleared, () => ({
      ...initialState,
      authStatus: 'anonymous',
    }));
  },
});

export const {
  authResolved,
  authFailed,
  profileReceived,
  profileFailed,
  cardsReceived,
  cardsFailed,
} = sessionSlice.actions;

export const selectSession = (state: RootState) => state.session;
export const selectSessionUser = (state: RootState) => state.session.user;
export const selectSessionProfile = (state: RootState) => state.session.profile;
export const selectSessionCards = (state: RootState) => state.session.cards;
export const selectSessionCardsStatus = (state: RootState) => state.session.cardsStatus;
export const selectSessionError = (state: RootState) => state.session.error;
export const selectSessionReady = createSelector(
  [selectSession],
  (session) => session.authStatus === 'anonymous'
    || (session.authStatus === 'authenticated' && session.profileStatus === 'succeeded'),
);

export default sessionSlice.reducer;
